
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useNotify } from '@/hooks/useNotify';
import { supabase } from '@/integrations/supabase/client';
import { Upload, FileText, X, Download } from 'lucide-react';

interface ContractPdfUploadProps {
  onFileUpload: (url: string | null) => void;
  currentFileUrl?: string;
  companyId: string;
  disabled?: boolean;
}

const ContractPdfUpload = ({ onFileUpload, currentFileUrl, companyId, disabled }: ContractPdfUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notify = useNotify();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      notify.warning("Please upload a PDF file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      notify.warning("PDF file size should be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const fileExt = 'pdf';
      const fileName = `${companyId}/contract-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('contract-documents')
        .upload(fileName, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('contract-documents')
        .getPublicUrl(fileName);

      setFileName(file.name);
      onFileUpload(publicUrl);

      notify.success("Contract PDF uploaded successfully");
    } catch (error) {
      console.error('Upload error:', error);
      notify.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setFileName(null);
    onFileUpload(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownload = () => {
    if (currentFileUrl) {
      window.open(currentFileUrl, '_blank');
    }
  };

  return (
    <div className="space-y-4">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".pdf"
        className="sr-only"
        disabled={disabled}
      />
      
      {currentFileUrl || fileName ? (
        <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
          <FileText className="h-8 w-8 text-red-600" />
          <div className="flex-1">
            <p className="text-sm font-medium">
              {fileName || 'Standard Contract Document'}
            </p>
            <p className="text-xs text-muted-foreground">PDF Document</p>
          </div>
          <div className="flex gap-2">
            {currentFileUrl && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
            {!disabled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemoveFile}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground mb-4">
            Upload a standard contract PDF document
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || disabled}
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload PDF'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ContractPdfUpload;
